import {
	AdvancedTable,
	Button,
	Divider,
	Form,
	Header,
	Input,
	Loader,
	Modal,
	Segment,
	Statistic,
} from '@mairistem/composants';
import * as React from 'react';
import DemandeStatusEnum, {
	type DemandeStatusType,
} from '../../enums/demandeStatusEnum';
import type { DemandeCompte } from '../../interfaces/demandeCompte';
import {
	getDemandesComptesByStatus,
	getStatistiquesDemandeCompte,
	type StatistiquesDemandeCompte,
} from '../../services/demandeCompteService';

const ERREURS_COLUMNS = [
	{ text: 'Code client', property: 'codeClient', sortable: true },
	{ text: 'Identifiant', property: 'identifiant', sortable: true },
	{ text: 'Anomalie', property: 'anomalie', sortable: true },
	{ text: 'Pastell', property: 'pastellLogin', sortable: true },
	{ text: 'Parapheur', property: 'parapheurLogin', sortable: true },
	{ text: 'Mail envoyé', property: 'mailEnvoye', sortable: true },
	{ text: 'User CIM', property: 'userCim', sortable: true },
	{ text: 'Message public', property: 'messagePublic', sortable: true },
	{ text: 'Message privé', property: 'messagePrive', sortable: true },
];

const parseStatus = (item: DemandeCompte) => {
	let parsed: Record<string, unknown> = {};
	try {
		parsed =
			typeof item.status === 'string'
				? JSON.parse(item.status)
				: (item.status ?? {});
	} catch {
		/* ignore */
	}

	return {
		...item,
		anomalie: (parsed.anomalie as string) || '',
		pastellLogin: (parsed.pastell as Record<string, string>)?.login || '',
		parapheurLogin: (parsed.parapheur as Record<string, string>)?.login || '',
		mailEnvoye: (parsed.mailEnvoye as string) || '',
		userCim: (parsed.userCim as string) || '',
		messagePublic: (parsed.messagePublic as string) || '',
		messagePrive: (parsed.messagePrive as string) || '',
	};
};

const STATUS_OPTIONS = Object.entries(DemandeStatusEnum).map(
	([key, value]) => ({
		key,
		text: value,
		value,
	}),
);

interface ModalAdminInterface {
	onValidate: (codeClient: string, refuseToBeAdmin: boolean) => void;
	onCancel?: () => void;
}

const ModalAdmin = ({ onValidate, onCancel }: ModalAdminInterface) => {
	const [codeClient, setCodeClient] = React.useState<string>('');
	const [refuseToBeAdmin, setrefuseToBeAdmin] = React.useState<boolean>(false);
	const [showErreurs, setShowErreurs] = React.useState<boolean>(false);
	const [isLoadingErreurs, setIsLoadingErreurs] =
		React.useState<boolean>(false);
	const [erreurs, setErreurs] = React.useState<DemandeCompte[]>([]);
	const [searchErreurs, setSearchErreurs] = React.useState<string>('');
	const [sort, setSort] = React.useState<Record<string, string>>({});
	const [selectedStatus, setSelectedStatus] = React.useState<DemandeStatusType>(
		DemandeStatusEnum.HS,
	);
	const [statistiques, setStatistiques] =
		React.useState<StatistiquesDemandeCompte | null>(null);
	const [isLoadingStatistiques, setIsLoadingStatistiques] =
		React.useState<boolean>(false);

	React.useEffect(() => {
		let cancelled = false;
		const fetchStats = async () => {
			setIsLoadingStatistiques(true);
			try {
				const response = (await getStatistiquesDemandeCompte('1973')) as any;
				const raw = response?.data ?? response;
				const stats = typeof raw === 'string' ? JSON.parse(raw) : raw;
				if (!cancelled) setStatistiques(stats);
			} catch {
				if (!cancelled) setStatistiques(null);
			} finally {
				if (!cancelled) setIsLoadingStatistiques(false);
			}
		};

		fetchStats();

		return () => {
			cancelled = true;
		};
	}, []);

	React.useEffect(() => {
		if (!showErreurs) {
			return undefined;
		}

		let cancelled = false;
		const fetchErreurs = async () => {
			setIsLoadingErreurs(true);
			try {
				const response = (await getDemandesComptesByStatus(
					selectedStatus,
					'1973',
				)) as any;
				const raw = response?.data ?? response;
				const data = Array.isArray(raw) ? raw.map(parseStatus) : [];
				if (!cancelled) setErreurs(data);
			} catch {
				if (!cancelled) setErreurs([]);
			} finally {
				if (!cancelled) setIsLoadingErreurs(false);
			}
		};

		fetchErreurs();

		return () => {
			cancelled = true;
		};
	}, [showErreurs, selectedStatus]);

	const handleChange = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ value }: { value: string },
	) => {
		setCodeClient(value);
	};

	const handleValidate = () => {
		onValidate(codeClient || '', refuseToBeAdmin);
	};

	const handleChangeRefuseAdmin = () => {
		setrefuseToBeAdmin(!refuseToBeAdmin);
	};

	const handleToggleShowErreurs = () => {
		const newValue = !showErreurs;
		setShowErreurs(newValue);

		if (!newValue) {
			setErreurs([]);
			setSearchErreurs('');
		}
	};

	const handleSearchErreursChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		setSearchErreurs(e.target.value);
	};

	const handleStatusChange = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ value }: { value: DemandeStatusType },
	) => {
		setSelectedStatus(value);
	};

	const filteredErreurs = React.useMemo(() => {
		let result = erreurs;

		if (searchErreurs) {
			const search = searchErreurs.toLowerCase();
			result = result.filter(
				(erreur: any) =>
					(erreur.codeClient || '').toLowerCase().includes(search) ||
					String(erreur.identifiant).toLowerCase().includes(search) ||
					(erreur.anomalie || '').toLowerCase().includes(search) ||
					(erreur.pastellLogin || '').toLowerCase().includes(search) ||
					(erreur.parapheurLogin || '').toLowerCase().includes(search) ||
					(erreur.mailEnvoye || '').toLowerCase().includes(search) ||
					(erreur.userCim || '').toLowerCase().includes(search) ||
					(erreur.messagePublic || '').toLowerCase().includes(search) ||
					(erreur.messagePrive || '').toLowerCase().includes(search),
			);
		}

		const sortEntries = Object.entries(sort);
		if (sortEntries.length > 0) {
			const [property, direction] = sortEntries[sortEntries.length - 1];
			result = [...result].sort((a, b) => {
				const valA = String((a as any)[property] ?? '').toLowerCase();
				const valB = String((b as any)[property] ?? '').toLowerCase();
				const cmp = valA.localeCompare(valB);
				return direction === 'asc' ? cmp : -cmp;
			});
		}

		return result;
	}, [erreurs, searchErreurs, sort]);

	return (
		<Modal
			open
			size={showErreurs ? 'large' : undefined}
			style={showErreurs ? { width: '80%' } : undefined}
		>
			<Modal.Header>Mode administrateur</Modal.Header>
			<Modal.Content scrolling>
				<div style={{ minHeight: showErreurs ? '70vh' : undefined }}>
					<Header textAlign="left" as="h3">
						{' '}
						Veuillez sélectionner un code client
					</Header>
					<Form>
						<Form.Input
							fluid
							name="codeClient"
							control={Input}
							data-testid="admin-codeclient"
							value={codeClient}
							onChange={handleChange}
							required
						/>
						<Form.Checkbox
							label="Je refuse le mode administrateur"
							checked={refuseToBeAdmin}
							onChange={handleChangeRefuseAdmin}
							data-testid="refuse-admin-checkbox"
						/>
						<Form.Checkbox
							label="Afficher le listing des erreurs"
							checked={showErreurs}
							onChange={handleToggleShowErreurs}
							data-testid="show-erreurs-checkbox"
						/>
					</Form>

					<Divider />
					<Header textAlign="left" as="h4">
						Statistiques des demandes
					</Header>
					{isLoadingStatistiques && (
						<Loader
							active
							inline="centered"
							content="Chargement des statistiques..."
						/>
					)}
					{!isLoadingStatistiques && !statistiques && (
						<Segment>Aucune statistique disponible</Segment>
					)}
					{!isLoadingStatistiques && statistiques && (
						<Segment>
							<Statistic.Group widths="4" size="small">
								<Statistic>
									<Statistic.Value>{statistiques.total}</Statistic.Value>
									<Statistic.Label>Total</Statistic.Label>
								</Statistic>
								<Statistic color="green">
									<Statistic.Value>{statistiques.ok}</Statistic.Value>
									<Statistic.Label>Terminé</Statistic.Label>
								</Statistic>
								<Statistic color="blue">
									<Statistic.Value>{statistiques.traitee}</Statistic.Value>
									<Statistic.Label>Traitées</Statistic.Label>
								</Statistic>
								<Statistic color="yellow">
									<Statistic.Value>{statistiques.encours}</Statistic.Value>
									<Statistic.Label>En cours</Statistic.Label>
								</Statistic>
							</Statistic.Group>
							<Statistic.Group
								widths="4"
								size="small"
								style={{ marginTop: '1em' }}
							>
								<Statistic color="orange">
									<Statistic.Value>{statistiques.nontraitee}</Statistic.Value>
									<Statistic.Label>Non traitées</Statistic.Label>
								</Statistic>
								<Statistic color="red">
									<Statistic.Value>{statistiques.hs}</Statistic.Value>
									<Statistic.Label>En anomalie</Statistic.Label>
								</Statistic>
								<Statistic color="grey">
									<Statistic.Value>{statistiques.abandon}</Statistic.Value>
									<Statistic.Label>Abandon</Statistic.Label>
								</Statistic>
								<Statistic color="teal">
									<Statistic.Value>{statistiques.autre}</Statistic.Value>
									<Statistic.Label>Autre</Statistic.Label>
								</Statistic>
							</Statistic.Group>
						</Segment>
					)}

					{showErreurs && (
						<>
							<Divider />
							<div
								style={{
									display: 'flex',
									gap: '1em',
									marginBottom: '1em',
									alignItems: 'center',
								}}
							>
								<div style={{ flex: 1 }}>
									<Input
										fluid
										icon="search"
										placeholder="Rechercher dans les erreurs..."
										value={searchErreurs}
										onChange={handleSearchErreursChange}
										data-testid="search-erreurs"
									/>
								</div>
								<Form.Select
									options={STATUS_OPTIONS}
									value={selectedStatus}
									onChange={handleStatusChange}
									data-testid="status-select"
								/>
							</div>
							{isLoadingErreurs ? (
								<Loader
									active
									inline="centered"
									content="Chargement des erreurs..."
								/>
							) : (
								<AdvancedTable
									columns={ERREURS_COLUMNS}
									data={filteredErreurs}
									onSort={setSort}
									onRowSelect={(item: DemandeCompte) =>
										setCodeClient(item?.codeClient ?? '')
									}
									// biome-ignore lint/complexity/noUselessFragments: On en a besoin
									header={<></>}
								/>
							)}
						</>
					)}
				</div>
			</Modal.Content>
			<Modal.Actions>
				{onCancel && (
					<Button onClick={onCancel} data-testid="annuler-admin-modal-btn">
						Annuler
					</Button>
				)}
				<Button
					onClick={handleValidate}
					disabled={!codeClient}
					data-testid="valider-admin-modal-btn"
					primary
				>
					Valider
				</Button>
			</Modal.Actions>
		</Modal>
	);
};
export default ModalAdmin;
